import React from "react";
import { timeToX } from "../utils/timelineCalculations";

interface PendingRangesProps {
  /** Stretches of the window whose events have not been fetched. */
  ranges: { from: number; to: number }[];
  /** Gated by `useDelayedLoading`, so a fast fetch never flashes a skeleton. */
  visible: boolean;
  min: number;
  max: number;
  scaledWidth: number;
  height: number;
}

/**
 * "We have not fetched this yet" — drawn across the tracks for the stretches the
 * client has never asked about.
 *
 * Deliberately its own visual language, because the timeline has three kinds of
 * absence and they mean different things to an operator:
 *
 *   this          — not fetched. Neutral, animated. Resolves in ~250 ms.
 *   video track   — recorded, not uploaded. Amber. Resolves in seconds.
 *   plain empty   — fetched, and nothing happened. Permanent.
 *
 * If they all looked the same, the one thing the operator could conclude is that
 * the interface is unreliable.
 */
export const PendingRanges: React.FC<PendingRangesProps> = ({
  ranges,
  visible,
  min,
  max,
  scaledWidth,
  height,
}) => {
  if (!visible || ranges.length === 0 || height <= 0) return null;

  return (
    <g pointerEvents="none" aria-hidden="true">
      <defs>
        <linearGradient id="timeline-pending-sheen" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.04" />
          <stop offset="50%" stopColor="currentColor" stopOpacity="0.12" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.04" />
        </linearGradient>
      </defs>
      {ranges.map((range) => {
        const startX = timeToX(range.from, min, max, scaledWidth);
        const endX = timeToX(range.to, min, max, scaledWidth);
        const width = endX - startX;
        if (!(width > 0)) return null;
        return (
          <rect
            key={`${range.from}-${range.to}`}
            className="timeline-pending"
            x={startX}
            y={0}
            width={width}
            height={height}
            fill="url(#timeline-pending-sheen)"
            stroke="currentColor"
            strokeOpacity={0.12}
            strokeDasharray="4 4"
            rx={2}
          />
        );
      })}
    </g>
  );
};
